/**
 * Nutrition Facts arithmetic: summing and scaling ingredient nutrients, FDA label rounding
 * (21 CFR 101.9(c)) and percent Daily Value (21 CFR 101.9(c)(8)(iv) and (c)(9), the 2016 values).
 *
 * Ingredient nutrients are stored per 100 g (ingredients.nutrition_per_100g). The database view
 * v_recipe_version_nutrition does the authoritative per-recipe sum; this module mirrors its rules
 * so a browser can scale a pizza to a slice and render the label.
 */

export const NUTRIENT_KEYS = [
  'calories',
  'fat_g',
  'saturated_fat_g',
  'trans_fat_g',
  'cholesterol_mg',
  'sodium_mg',
  'carbohydrate_g',
  'fiber_g',
  'sugars_g',
  'added_sugars_g',
  'protein_g',
  'vitamin_d_mcg',
  'calcium_mg',
  'iron_mg',
  'potassium_mg',
] as const;
export type NutrientKey = (typeof NUTRIENT_KEYS)[number];
export type Nutrients = Partial<Record<NutrientKey, number>>;

/** Keys an ingredient must have before a recipe using it counts as complete. */
export const REQUIRED_NUTRIENT_KEYS = [
  'calories',
  'fat_g',
  'saturated_fat_g',
  'trans_fat_g',
  'cholesterol_mg',
  'sodium_mg',
  'carbohydrate_g',
  'fiber_g',
  'sugars_g',
  'protein_g',
] as const satisfies readonly NutrientKey[];

export const NUTRIENT_UNITS: Record<NutrientKey, 'kcal' | 'g' | 'mg' | 'mcg'> = {
  calories: 'kcal',
  fat_g: 'g',
  saturated_fat_g: 'g',
  trans_fat_g: 'g',
  cholesterol_mg: 'mg',
  sodium_mg: 'mg',
  carbohydrate_g: 'g',
  fiber_g: 'g',
  sugars_g: 'g',
  added_sugars_g: 'g',
  protein_g: 'g',
  vitamin_d_mcg: 'mcg',
  calcium_mg: 'mg',
  iron_mg: 'mg',
  potassium_mg: 'mg',
};

export const NUTRIENT_LABELS: Record<NutrientKey, string> = {
  calories: 'Calories',
  fat_g: 'Total Fat',
  saturated_fat_g: 'Saturated Fat',
  trans_fat_g: 'Trans Fat',
  cholesterol_mg: 'Cholesterol',
  sodium_mg: 'Sodium',
  carbohydrate_g: 'Total Carbohydrate',
  fiber_g: 'Dietary Fiber',
  sugars_g: 'Total Sugars',
  added_sugars_g: 'Added Sugars',
  protein_g: 'Protein',
  vitamin_d_mcg: 'Vitamin D',
  calcium_mg: 'Calcium',
  iron_mg: 'Iron',
  potassium_mg: 'Potassium',
};

/** FDA Daily Values for adults and children 4 years and older. */
export const DAILY_VALUES: Partial<Record<NutrientKey, number>> = {
  fat_g: 78,
  saturated_fat_g: 20,
  cholesterol_mg: 300,
  sodium_mg: 2300,
  carbohydrate_g: 275,
  fiber_g: 28,
  added_sugars_g: 50,
  vitamin_d_mcg: 20,
  calcium_mg: 1300,
  iron_mg: 18,
  potassium_mg: 4700,
};

const MICRONUTRIENTS: ReadonlySet<NutrientKey> = new Set([
  'vitamin_d_mcg',
  'calcium_mg',
  'iron_mg',
  'potassium_mg',
]);

export function isNutrientKey(key: string): key is NutrientKey {
  return (NUTRIENT_KEYS as readonly string[]).includes(key);
}

/**
 * Validate a nutrition_per_100g object: only known keys, each a finite number ≥ 0.
 * Returns the cleaned object, or the list of problems.
 */
export function parseNutrients(
  input: unknown,
): { ok: true; value: Nutrients } | { ok: false; errors: string[] } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, errors: ['Nutrition must be an object of per-100 g values'] };
  }
  const errors: string[] = [];
  const value: Nutrients = {};
  for (const [key, raw] of Object.entries(input)) {
    if (!isNutrientKey(key)) {
      errors.push(`Unknown nutrient: ${key}`);
      continue;
    }
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) {
      errors.push(`${NUTRIENT_LABELS[key]} must be a number of zero or more`);
      continue;
    }
    value[key] = raw;
  }
  return errors.length ? { ok: false, errors } : { ok: true, value };
}

/** Required keys this nutrient set lacks. Null or undefined lacks all of them. */
export function missingNutrientKeys(n: Nutrients | null | undefined): NutrientKey[] {
  return REQUIRED_NUTRIENT_KEYS.filter((k) => typeof n?.[k] !== 'number');
}

export function isNutritionComplete(n: Nutrients | null | undefined): boolean {
  return missingNutrientKeys(n).length === 0;
}

export function scaleNutrients(n: Nutrients, factor: number): Nutrients {
  if (!Number.isFinite(factor) || factor < 0) {
    throw new Error(`Scale factor must be zero or more, got ${factor}`);
  }
  const out: Nutrients = {};
  for (const k of NUTRIENT_KEYS) {
    const v = n[k];
    if (typeof v === 'number') out[k] = v * factor;
  }
  return out;
}

/** Nutrients in `grams` of an ingredient whose values are per 100 g. */
export function nutrientsForGrams(per100g: Nutrients, grams: number): Nutrients {
  return scaleNutrients(per100g, grams / 100);
}

/**
 * Add nutrient sets. A key appears in the total only when every part reports it, so a total is
 * never a confident partial (the same rule as v_recipe_version_nutrition).
 */
export function sumNutrients(parts: readonly Nutrients[]): Nutrients {
  const out: Nutrients = {};
  if (parts.length === 0) return out;
  for (const k of NUTRIENT_KEYS) {
    let total = 0;
    let everywhere = true;
    for (const p of parts) {
      const v = p[k];
      if (typeof v !== 'number') {
        everywhere = false;
        break;
      }
      total += v;
    }
    if (everywhere) out[k] = total;
  }
  return out;
}

export interface LabelAmount {
  /** The number printed (for "less than" amounts, the threshold). */
  value: number;
  /** Ready to print: "230", "2.5 g", "less than 5 mg", "0 g". */
  text: string;
  lessThan: boolean;
}

function nearest(value: number, increment: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(increment)) + 1);
  // Pre-round the step count so 0.35 / 0.1 (3.4999…) rounds half up like the decimal it is.
  const steps = Math.round(Number((value / increment).toFixed(9)));
  return Number((steps * increment).toFixed(decimals));
}

function amount(value: number, unit: string, lessThan = false): LabelAmount {
  const shown = unit === 'kcal' ? `${value}` : `${value} ${unit}`;
  return { value, text: lessThan ? `less than ${shown}` : shown, lessThan };
}

/** Round a declared amount the way 21 CFR 101.9(c) requires. */
export function roundForLabel(key: NutrientKey, raw: number): LabelAmount {
  const v = Math.max(0, raw);
  const unit = NUTRIENT_UNITS[key];
  switch (key) {
    case 'calories':
      if (v < 5) return amount(0, unit);
      return amount(v <= 50 ? nearest(v, 5) : nearest(v, 10), unit);
    case 'fat_g':
    case 'saturated_fat_g':
    case 'trans_fat_g':
      if (v < 0.5) return amount(0, unit);
      return amount(v < 5 ? nearest(v, 0.5) : nearest(v, 1), unit);
    case 'cholesterol_mg':
      if (v < 2) return amount(0, unit);
      if (v <= 5) return amount(5, unit, true);
      return amount(nearest(v, 5), unit);
    case 'sodium_mg':
      if (v < 5) return amount(0, unit);
      return amount(v <= 140 ? nearest(v, 5) : nearest(v, 10), unit);
    case 'carbohydrate_g':
    case 'fiber_g':
    case 'sugars_g':
    case 'added_sugars_g':
    case 'protein_g':
      if (v < 0.5) return amount(0, unit);
      if (v < 1) return amount(1, unit, true);
      return amount(nearest(v, 1), unit);
    // Vitamins and minerals: absolute amounts to the FDA increments.
    case 'vitamin_d_mcg':
    case 'iron_mg':
      return amount(nearest(v, 0.1), unit);
    case 'calcium_mg':
    case 'potassium_mg':
      return amount(nearest(v, 10), unit);
  }
}

/**
 * Percent Daily Value, rounded for the label. Null for nutrients without a Daily Value
 * (calories, trans fat, total sugars, protein here). Macronutrients go to the nearest 1 %;
 * vitamins and minerals: below 2 % is 0, then 2 % steps to 10 %, 5 % steps to 50 %, 10 % steps
 * above. Computed from the unrounded amount; an amount that labels as 0 is 0 %.
 */
export function percentDailyValue(key: NutrientKey, raw: number): number | null {
  const dv = DAILY_VALUES[key];
  if (dv === undefined) return null;
  const label = roundForLabel(key, raw);
  if (label.value === 0 && !label.lessThan) return 0;
  const pct = (Math.max(0, raw) / dv) * 100;
  if (!MICRONUTRIENTS.has(key)) return Math.round(pct);
  if (pct < 2) return 0;
  if (pct <= 10) return nearest(pct, 2);
  if (pct <= 50) return nearest(pct, 5);
  return nearest(pct, 10);
}

export interface NutritionLabelRow {
  key: NutrientKey;
  label: string;
  amount: LabelAmount;
  dailyValuePct: number | null;
}

/** Label rows for the nutrients present, in Nutrition Facts order. */
export function nutritionLabel(n: Nutrients): NutritionLabelRow[] {
  const rows: NutritionLabelRow[] = [];
  for (const key of NUTRIENT_KEYS) {
    const v = n[key];
    if (typeof v !== 'number') continue;
    rows.push({
      key,
      label: NUTRIENT_LABELS[key],
      amount: roundForLabel(key, v),
      dailyValuePct: percentDailyValue(key, v),
    });
  }
  return rows;
}
